import React from "react";

import { motion } from "framer-motion";

import {
  Laptop,
  GraduationCap,
  Trophy,
  Clock3,
  HeartHandshake,
  Coffee,
  ArrowUpRight,
} from "lucide-react";

import AnimatedHeading from "../../../animations/AnimatedHeading";

const icons = [
  Laptop,
  GraduationCap,
  Trophy,
  Clock3,
  HeartHandshake,
  Coffee,
];

const Benefits = ({ job }) => {
  if (!job?.benefits?.length) return null;

  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-2
        font-saira
        sm:py-4
        lg:py-6
      "
    >
      {/* ============================================================
          BACKGROUND GLOWS
      ============================================================ */}

      {/* Left Glow */}
      <motion.div
        animate={{
          x: [-20, 20, -20],
          y: [-15, 15, -15],
        }}
        transition={{
          duration: 14,
          repeat: Infinity,
          ease: "linear",
        }}
        className="
          pointer-events-none
          absolute
          -left-20
          top-0
          h-64
          w-64
          rounded-full
          bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
          blur-[110px]
          sm:h-80
          sm:w-80
          sm:blur-[130px]
        "
      />

      {/* Right Glow */}
      <motion.div
        animate={{
          x: [20, -20, 20],
          y: [15, -15, 15],
        }}
        transition={{
          duration: 16,
          repeat: Infinity,
          ease: "linear",
        }}
        className="
          pointer-events-none
          absolute
          -right-20
          bottom-0
          h-64
          w-64
          rounded-full
          bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
          blur-[110px]
          sm:h-80
          sm:w-80
          sm:blur-[130px]
        "
      />

      {/* ============================================================
          CONTAINER
      ============================================================ */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* ============================================================
            HEADING
        ============================================================ */}

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
          }}
          className="
            mb-10
            text-center
            sm:mb-12
          "
        >
          {/* Section Badge */}
          <div
            className="
              inline-flex
              items-center
              rounded-full
              border
              border-[color-mix(in_srgb,var(--brand-primary)_20%,transparent)]
              bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
              px-4
              py-2
            "
          >
            <span
              className="
                text-xs
                font-semibold
                uppercase
                tracking-[0.25em]
                text-[var(--brand-light)]
                sm:text-sm
              "
            >
              EMPLOYEE BENEFITS
            </span>
          </div>

          {/* Heading */}
          <div>
            <AnimatedHeading
              title="Perks & Benefits"
              highlight="Benefits"
            />
          </div>

          {/* Description */}
          <p
            className="
              mx-auto
              max-w-7xl
              text-base
              leading-8
              text-[var(--text-secondary)]
              sm:text-lg
              sm:leading-9
            "
          >
            We believe exceptional work deserves exceptional rewards. Our
            benefits are designed to support your professional growth,
            promote work-life balance, and provide the resources you need
            to build a successful and fulfilling career with WebBocket.
          </p>
        </motion.div>

        {/* ============================================================
            BENEFITS GRID
        ============================================================ */}

        <div
          className="
            grid
            grid-cols-1
            gap-4
            sm:gap-5
            lg:grid-cols-2
          "
        >
          {job.benefits.map((benefit, index) => {
            const Icon = icons[index % icons.length];

            const title =
              typeof benefit === "string"
                ? benefit
                : benefit.title;

            const description =
              typeof benefit === "string"
                ? ""
                : benefit.description;

            return (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 25,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.08,
                }}
                whileHover={{
                  y: -5,
                }}
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-2xl
                  border
                  border-[var(--border-primary)]
                  bg-[var(--card-bg)]
                  p-5
                  transition-all
                  duration-300
                  hover:border-[color-mix(in_srgb,var(--brand-primary)_40%,transparent)]
                  hover:bg-[var(--card-bg-secondary)]
                  sm:p-6
                "
              >
                {/* ==================================================
                    HOVER GLOW
                ================================================== */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-[radial-gradient(circle_at_top_right,color-mix(in_srgb,var(--brand-primary)_13%,transparent),transparent_70%)]
                    opacity-0
                    transition-opacity
                    duration-500
                    group-hover:opacity-100
                  "
                />

                {/* ==================================================
                    LEFT ACCENT
                ================================================== */}

                <div
                  className="
                    absolute
                    left-0
                    top-0
                    h-full
                    w-[3px]
                    bg-[var(--brand-primary)]
                  "
                />

                {/* ==================================================
                    CARD CONTENT
                ================================================== */}

                <div
                  className="
                    relative
                    z-10
                    flex
                    items-start
                    gap-4
                    sm:gap-5
                  "
                >
                  {/* ==================================================
                      ICON
                  ================================================== */}

                  <div
                    className="
                      flex
                      h-11
                      w-11
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
                      text-[var(--brand-primary)]
                      transition-all
                      duration-300
                      group-hover:bg-[var(--brand-primary)]
                      group-hover:text-white
                      sm:h-12
                      sm:w-12
                      sm:rounded-2xl
                    "
                  >
                    <Icon
                      size={20}
                      className="sm:h-[22px] sm:w-[22px]"
                    />
                  </div>

                  {/* ==================================================
                      TEXT CONTENT
                  ================================================== */}

                  <div className="flex-1">
                    <div
                      className="
                        flex
                        items-start
                        justify-between
                        gap-3
                      "
                    >
                      {/* Benefit Title */}
                      <p
                        className="
                          text-[15px]
                          leading-7
                          text-[var(--text-secondary)]
                          sm:text-base
                          sm:leading-8
                        "
                      >
                        {title}
                      </p>

                      {/* Arrow */}
                      <ArrowUpRight
                        size={18}
                        className="
                          mt-1
                          shrink-0
                          text-[var(--text-muted)]
                          transition-all
                          duration-300
                          group-hover:-translate-y-1
                          group-hover:translate-x-1
                          group-hover:text-[var(--brand-primary)]
                        "
                      />
                    </div>

                    {/* Benefit Description */}
                    {description && (
                      <p
                        className="
                          mt-2
                          text-[15px]
                          leading-7
                          text-[var(--text-muted)]
                          sm:text-base
                          sm:leading-8
                        "
                      >
                        {description}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Benefits;